import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createBrowserRouter, Link, RouterProvider, useLocation } from 'react-router-dom';
import { api, ApiError, Alert, Icon, Spinner } from '@pryme/ui';
import { Layout } from './components/Layout';
import { DevHeader } from './components/Header';
import { ServiceLayout } from './components/ServiceLayout';
import { DevProvider } from './lib/context';
import { userPortalUrl } from './lib/portal';
import type { MeInfo } from './lib/types';
import { SignIn } from './pages/SignIn';
import { Dashboard } from './pages/Dashboard';
import { CreateService } from './pages/CreateService';
import { ServiceOverview } from './pages/ServiceOverview';
import { Credentials } from './pages/Credentials';
import { Redirects } from './pages/Redirects';
import { Claims } from './pages/Claims';
import { Security } from './pages/Security';
import { SdkIntegration } from './pages/SdkIntegration';
import { Playground } from './pages/Playground';
import { MandatePlayground } from './pages/MandatePlayground';
import { Events } from './pages/Events';
import { Domains } from './pages/Domains';
import { Team } from './pages/Team';
import { InviteAccept } from './pages/InviteAccept';
import { Docs } from './pages/Docs';
import { NotFound } from './pages/NotFound';

type Gate =
  | { phase: 'loading' }
  | { phase: 'error'; error: unknown }
  | { phase: 'anon'; environment: string }
  | { phase: 'inactive'; environment: string; me: MeInfo | null; error?: unknown }
  | { phase: 'ready'; environment: string; me: MeInfo };

/** Shell for signed-out visitors: the same header, no sidebar. `bare` pages (the landing) lay out their own full-width sections. */
function PublicShell(props: { environment?: string; children: ReactNode; bare?: boolean }) {
  return (
    <div className={`p-shell dv-shell ${props.environment === 'production' ? '' : 'dv-sandbox'}`}>
      <a href="#main" className="p-skip">Skip to content</a>
      <DevHeader environment={props.environment}>
        <Link to="/docs" className="p-topbar-link p-hide-sm">Documentation</Link>
      </DevHeader>
      <div>
        {props.bare ? <main id="main">{props.children}</main> : <main className="p-main" id="main">{props.children}</main>}
        <footer className="p-footer">
          <div className="p-footer-inner">
            <span>Pryme · Programmable. Verifiable. Me.</span>
            <span>Independent identity and delegation infrastructure. Not a government service.</span>
          </div>
        </footer>
      </div>
    </div>
  );
}

/** Session gate: developers are ACTIVE verified Pryme humans signed in with their passkey. */
function Root() {
  const [gate, setGate] = useState<Gate>({ phase: 'loading' });
  const location = useLocation();

  const load = useCallback(async () => {
    try {
      const s = await api<{ authenticated: boolean; environment: string }>('/v1/auth/session');
      if (!s.authenticated) return setGate({ phase: 'anon', environment: s.environment });
      try {
        const me = await api<MeInfo>('/v1/me');
        if (me.status !== 'active') return setGate({ phase: 'inactive', environment: s.environment, me });
        setGate({ phase: 'ready', environment: s.environment, me });
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return setGate({ phase: 'anon', environment: s.environment });
        setGate({ phase: 'inactive', environment: s.environment, me: null, error: e });
      }
    } catch (e) {
      setGate({ phase: 'error', error: e });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (gate.phase === 'loading') return <div className="p-main"><Spinner label="Loading Pryme Developers…" /></div>;
  if (gate.phase === 'error') {
    return (
      <PublicShell>
        <div style={{ maxWidth: 560, margin: '2rem auto' }}>
          <Alert tone="bad">
            Pryme is unreachable right now. Nothing was changed.{' '}
            <button className="p-btn secondary small" onClick={() => void load()}>Retry</button>
          </Alert>
        </div>
      </PublicShell>
    );
  }
  if (gate.phase === 'anon') {
    if (location.pathname === '/docs') return <PublicShell environment={gate.environment}><Docs /></PublicShell>;
    return <PublicShell environment={gate.environment} bare><SignIn onSignedIn={load} invite={location.pathname === '/invite'} /></PublicShell>;
  }
  if (gate.phase === 'inactive') {
    return (
      <PublicShell environment={gate.environment}>
        <section className="p-card" style={{ maxWidth: 600, margin: '2rem auto' }}>
          <span className="p-eyebrow">Verification required</span>
          <h1>Your Pryme ID is not active yet</h1>
          <p>
            The developer site is open to people with an <strong>active, verified Pryme ID</strong>. Your identity status is{' '}
            <code className="p-mono-chip">{gate.me?.displayStatus ?? 'pending_verification'}</code>.
          </p>
          <p>Finish verification and create your first passkey in the Pryme portal, then return here.</p>
          <div className="p-row">
            <a className="p-btn" href={userPortalUrl('/enroll')}>Continue verification <Icon name="arrowRight" size={15} /></a>
            <button className="p-btn secondary" onClick={async () => { await api('/v1/auth/logout', { method: 'POST' }).catch(() => undefined); void load(); }}>Sign out</button>
          </div>
        </section>
      </PublicShell>
    );
  }
  return (
    <DevProvider environment={gate.environment} me={gate.me} onLogout={() => setGate({ phase: 'anon', environment: gate.environment })}>
      <Layout />
    </DevProvider>
  );
}

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <Root />,
      children: [
        { index: true, element: <Dashboard /> },
        { path: 'services/new', element: <CreateService /> },
        {
          path: 'services/:serviceId',
          element: <ServiceLayout />,
          children: [
            { index: true, element: <ServiceOverview /> },
            { path: 'credentials', element: <Credentials /> },
            { path: 'redirects', element: <Redirects /> },
            { path: 'claims', element: <Claims /> },
            { path: 'security', element: <Security /> },
            { path: 'sdk', element: <SdkIntegration /> },
            { path: 'playground', element: <Playground /> },
            { path: 'mandates', element: <MandatePlayground /> },
            { path: 'events', element: <Events /> },
          ],
        },
        { path: 'domains', element: <Domains /> },
        { path: 'team', element: <Team /> },
        { path: 'invite', element: <InviteAccept /> },
        { path: 'docs', element: <Docs /> },
        { path: '*', element: <NotFound /> },
      ],
    },
  ]
);

export function App() {
  return <RouterProvider router={router} />;
}
